var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { styled, useThemeProps } from '@mui/material/styles';
import { unstable_composeClasses as composeClasses } from '@mui/utils';
import { PickersArrowSwitcher, useLocaleText, useUtils, } from '@mui/x-date-pickers/internals';
import { useNextMonthDisabled, usePreviousMonthDisabled, } from '@mui/x-date-pickers/internals/hooks/date-helpers-hooks';
import { getPickersCalendarHeaderUtilityClass } from '@mui/x-date-pickers/PickersCalendarHeader/pickersCalendarHeaderClasses';
import clsx from 'clsx';
import React from 'react';
import Button from '../Button';
var useUtilityClasses = function (ownerState) {
    var classes = ownerState.classes;
    var slots = {
        root: ['root'],
        labelContainer: ['labelContainer'],
        label: ['label'],
        switchViewButton: ['switchViewButton'],
        switchViewIcon: ['switchViewIcon'],
    };
    return composeClasses(slots, getPickersCalendarHeaderUtilityClass, classes);
};
var PickersCalendarHeaderRoot = styled('div', {
    name: 'MuiPickersCalendarHeader',
    slot: 'Root',
    overridesResolver: function (_, styles) { return styles.root; },
})({
    display: 'flex',
    alignItems: 'center',
    marginTop: 16,
    marginBottom: 8,
    paddingLeft: 24,
    paddingRight: 12,
    // prevent jumping in safari
    maxHeight: 30,
    minHeight: 30,
});
var PickersCalendarHeaderLabelContainer = styled('div', {
    name: 'MuiPickersCalendarHeader',
    slot: 'LabelContainer',
    overridesResolver: function (_, styles) { return styles.labelContainer; },
})(function (_a) {
    var theme = _a.theme;
    return (__assign(__assign({ display: 'flex', overflow: 'hidden', alignItems: 'center', marginRight: 'auto' }, theme.typography.body1), { fontWeight: theme.typography.fontWeightMedium }));
});
var PickersCalendarHeaderLabel = styled('div', {
    name: 'MuiPickersCalendarHeader',
    slot: 'Label',
    overridesResolver: function (_, styles) { return styles.label; },
})({
    marginRight: 6,
});
/**
 * If `clickable` prop is `true`, returns a button element that is clickable and changes the view. Otherwise returns a div element.
 */
var HeaderLabelWrapper = function (_a) {
    var clickable = _a.clickable, children = _a.children;
    if (clickable) {
        return React.createElement(Button, { color: "tertiary" }, children);
    }
    return React.createElement(React.Fragment, null, children);
};
/**
 * Component that will be displayed in the upper part of the DatePicker / DateTimePicker
 * Code taken from: https://github.com/mui/mui-x/blob/b958c6d0ff6baa8098019bdd2d391854647eed91/packages/x-date-pickers/src/PickersCalendarHeader/PickersCalendarHeader.tsx#L15
 */
export var PickersCalendarHeader = React.forwardRef(function PickersCalendarHeader(inProps, ref) {
    var localeText = useLocaleText();
    var utils = useUtils();
    var props = useThemeProps({ props: inProps, name: 'MuiPickersCalendarHeader' });
    var slots = props.slots, slotProps = props.slotProps, month = props.currentMonth, disabled = props.disabled, disableFuture = props.disableFuture, disablePast = props.disablePast, maxDate = props.maxDate, minDate = props.minDate, onMonthChange = props.onMonthChange, onViewChange = props.onViewChange, view = props.view, reduceAnimations = props.reduceAnimations, views = props.views, labelId = props.labelId, className = props.className, timezone = props.timezone, other = __rest(props, ["slots", "slotProps", "currentMonth", "disabled", "disableFuture", "disablePast", "maxDate", "minDate", "onMonthChange", "onViewChange", "view", "reduceAnimations", "views", "labelId", "className", "timezone"]);
    var ownerState = props;
    var classes = useUtilityClasses(props);
    var selectNextMonth = function () { return onMonthChange(utils.addMonths(month, 1), 'left'); };
    var selectPreviousMonth = function () { return onMonthChange(utils.addMonths(month, -1), 'right'); };
    var isNextMonthDisabled = useNextMonthDisabled(month, {
        disableFuture: disableFuture,
        maxDate: maxDate,
        timezone: timezone,
    });
    var isPreviousMonthDisabled = usePreviousMonthDisabled(month, {
        disablePast: disablePast,
        minDate: minDate,
        timezone: timezone,
    });
    var handleToggleView = function () {
        if (views.length === 1 || !onViewChange || disabled) {
            return;
        }
        if (views.length === 2) {
            onViewChange(views.find(function (el) { return el !== view; }) || views[0]);
        }
        else {
            // switching only between first 2
            var nextIndexToOpen = views.indexOf(view) !== 0 ? 0 : 1;
            onViewChange(views[nextIndexToOpen]);
        }
    };
    // No need to display more information
    if (views.length === 1 && views[0] === 'year') {
        return null;
    }
    return (React.createElement(PickersCalendarHeaderRoot, __assign({}, other, { ownerState: ownerState, className: clsx(className, classes.root), ref: ref }),
        React.createElement(PickersArrowSwitcher, { slots: slots, slotProps: slotProps, onGoToPrevious: selectPreviousMonth, isPreviousDisabled: isPreviousMonthDisabled, previousLabel: localeText.previousMonth, onGoToNext: selectNextMonth, isNextDisabled: isNextMonthDisabled, nextLabel: localeText.nextMonth, "data-current-view": view },
            React.createElement(PickersCalendarHeaderLabelContainer, { role: "presentation", onClick: handleToggleView, ownerState: ownerState, "aria-live": "polite", className: classes.labelContainer },
                React.createElement(HeaderLabelWrapper, { clickable: views.length > 1 },
                    React.createElement(PickersCalendarHeaderLabel, { id: labelId, ownerState: ownerState, className: classes.label }, utils.format(month, 'monthAndYear')))))));
});
